import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

/**
 * G.2: Webinar registration thank-you page.
 * Shown after successful registration. Displays:
 * - Confirmation message with webinar details
 * - .ics calendar download button
 * - "Add to Calendar" links (Google, Outlook)
 * - Join link (magic token sent via email)
 * - Social sharing buttons
 */
export default function WebinarThankYouPage() {
  const [params] = useSearchParams();
  const webinarTitle = params.get("title") ?? "the webinar";
  const webinarDate = params.get("date") ?? "";
  const webinarTime = params.get("time") ?? "";
  const tenantSlug = params.get("tenantSlug");
  const webinarSlug = params.get("webinarSlug");
  const providedShareUrl = params.get("shareUrl");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");

  const shareUrl = useMemo(() => {
    if (providedShareUrl) {
      return providedShareUrl;
    }

    if (tenantSlug && webinarSlug && typeof window !== "undefined") {
      return `${window.location.origin}/webinar/${tenantSlug}/${webinarSlug}`;
    }

    if (typeof window !== "undefined") {
      return window.location.origin;
    }

    return "";
  }, [providedShareUrl, tenantSlug, webinarSlug]);

  const shareText = `I just attended ${webinarTitle}!`;

  useEffect(() => {
    if (copyState !== "copied") {
      return undefined;
    }

    const timer = setTimeout(() => setCopyState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [copyState]);

  const handleDownloadIcs = () => {
    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `SUMMARY:${webinarTitle}`,
      `DTSTART:${webinarDate.replace(/-/g, "")}T${webinarTime.replace(/:/g, "")}00Z`,
      "DURATION:PT1H",
      "DESCRIPTION:Join link will be sent via email",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");

    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${webinarTitle.replace(/\s+/g, "-").toLowerCase()}.ics`;
    link.click();
    URL.revokeObjectURL(url);
  };

  async function handleCopyLink() {
    if (!shareUrl || typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      setCopyState("error");
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  }

  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(webinarTitle)}&dates=${webinarDate.replace(/-/g, "")}/${webinarDate.replace(/-/g, "")}&details=${encodeURIComponent("Join link will be sent via email")}`;
  const outlookCalendarUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(webinarTitle)}&startdt=${encodeURIComponent(webinarDate)}`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-8 text-center shadow-lg">
        <div className="mb-4 text-4xl">🎉</div>
        <h1 className="mb-2 text-2xl font-bold text-foreground">You're Registered!</h1>
        <p className="mb-6 text-muted-foreground">
          You're all set for <strong>{webinarTitle}</strong>.
          {webinarDate && (
            <>
              <br />
              {webinarDate} {webinarTime && `at ${webinarTime}`}
            </>
          )}
        </p>

        <div className="mb-6 rounded-lg bg-muted p-4 text-sm text-muted-foreground">
          A confirmation email with your join link has been sent.
          Check your inbox (and spam folder).
        </div>

        <div className="space-y-3">
          <button
            onClick={handleDownloadIcs}
            className="w-full rounded-md border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent"
          >
            📅 Download Calendar Invite (.ics)
          </button>

          <div className="flex gap-2">
            <a
              href={googleCalendarUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 rounded-md border border-border px-3 py-2 text-center text-sm text-foreground hover:bg-accent"
            >
              Google Calendar
            </a>
            <a
              href={outlookCalendarUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 rounded-md border border-border px-3 py-2 text-center text-sm text-foreground hover:bg-accent"
            >
              Outlook
            </a>
          </div>
        </div>

        <div className="mt-6 border-t border-border pt-4">
          <p className="mb-2 text-xs text-muted-foreground">Share with your network</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={twitterShareUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
              Twitter
            </a>
            <a href={linkedInShareUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
              LinkedIn
            </a>
            <a href={facebookShareUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
              Facebook
            </a>
            <button type="button" onClick={() => void handleCopyLink()} className="text-sm text-primary hover:underline">
              {copyState === "copied" ? "Copied!" : "Copy link"}
            </button>
          </div>
          {shareUrl ? (
            <p className="mt-3 break-all text-xs text-muted-foreground">{shareUrl}</p>
          ) : null}
          {copyState === "error" ? (
            <p className="mt-2 text-xs text-[var(--color-destructive)]">Unable to copy the registration link.</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
